import type { TFunction } from 'i18next'
import dayjs from 'dayjs'
import { MiwaLockStatus, MiwaLockWebsocketResponseStatusCode } from '@/constants/miwa-lock'
import { TMiwaReadCardResult } from '@/models/miwa-lock'

export type TReadCard = {
  roomNumber: string
  // カードに書き込まれた有効期間（YYYY-MM-DD HH:mm または YYYY-MM-DD）
  startAt: string
  endAt: string
}

export const toMiwaAppErrorMessage = (statusCode: number, t: TFunction): string => {
  switch (statusCode) {
    case MiwaLockWebsocketResponseStatusCode.UNAUTHENTICATED:
      return t('Verification failed. Please check and try again.')
    case MiwaLockWebsocketResponseStatusCode.INVALID_DATA:
      return t('Unable to issue card. Please try again.')
    case MiwaLockWebsocketResponseStatusCode.CONNECTION_ERROR:
      return t('Cannot connect to network. Please try again.')
    case MiwaLockWebsocketResponseStatusCode.INVALID_PC_ID:
      return t('PCID mismatch. Please check and try again.')
    case MiwaLockWebsocketResponseStatusCode.WRITE_CARD_ERROR:
      return t('Unable to read card. Check your reading window app terminal.')
    case MiwaLockWebsocketResponseStatusCode.NO_CARD_INSERTED:
      return t('Unable to read card. Please hold your card over.')
    case MiwaLockWebsocketResponseStatusCode.NOT_SUPPORTED_CARD:
      return t('Unable to read card. Please check your card.')
    case MiwaLockWebsocketResponseStatusCode.SERVER_ERROR:
      return t('Server error')
    case MiwaLockWebsocketResponseStatusCode.DEVICE_BUSY:
      return t('The NFC is processing another card issue. Please try again after the process is complete.')
    default:
      return t('Unable to read card. Please try again.')
  }
}

/**
 * 読取り結果からカード情報を取り出す。未発行のカードなど部屋番号・有効期間が揃わない場合は null
 */
export const parseReadCardResult = (body: TMiwaReadCardResult): TReadCard | null => {
  const roomNumber = body.room_number === null || body.room_number === undefined ? '' : String(body.room_number).trim()
  const startAt = body.checkin_date ?? ''
  const endAt = body.checkout_date ?? ''
  if (!roomNumber || !dayjs(startAt).isValid() || !dayjs(endAt).isValid()) return null

  return { roomNumber, startAt, endAt }
}

// 部屋割の照会はカードの日付だけで行うため、時刻を落とす
export const toCardDate = (dateTime: string): string => dayjs(dateTime).format('YYYY-MM-DD')

/**
 * 新規発行で、部屋割の IN 日当日以降に発行する場合の利用開始日時（発行時点）。それ以外は null（予定日時のまま）
 * セルフ CI と同じく発行した時点から有効にする。チェックイン予定より前なら予定時刻まで開かない、後なら過去の予定時刻が書き込まれるのを避けるため
 * IN 日より前に発行した場合は、IN 日前から部屋に入れてしまわないよう予定日時のままにする
 */
export const calcIssueStartAt = (issueType: string, plannedCheckinDate: string, now: dayjs.Dayjs = dayjs()): dayjs.Dayjs | null => {
  if (issueType !== MiwaLockStatus.ISSUE || !plannedCheckinDate) return null
  return now.isBefore(plannedCheckinDate, 'day') ? null : now
}

/**
 * カードに書き込む開始日時（YYYY-MM-DD HH:mm）
 * 再発行は前のカードより新しい開始日時にするため最終発行日時の 1 分後、追加発行は最終発行日時をそのまま使う
 */
export const calcCardStartAt = (
  type: string,
  { checkinDate, checkinTime, lastCheckinDatetime }: { checkinDate: string; checkinTime: string; lastCheckinDatetime: string },
): string => {
  switch (type) {
    case MiwaLockStatus.ISSUE:
      return dayjs(`${checkinDate} ${checkinTime}`).format('YYYY-MM-DD HH:mm')
    case MiwaLockStatus.REISSUE:
      return dayjs(lastCheckinDatetime).add(1, 'm').format('YYYY-MM-DD HH:mm')
    case MiwaLockStatus.ADDNEWISSUE:
      return dayjs(lastCheckinDatetime).format('YYYY-MM-DD HH:mm')
    default:
      return ''
  }
}

/**
 * 部屋割のチェックアウト日と時刻を、カードの終了日・時刻に分ける
 * 24 時以降の時刻（例 25:00）は翌日に繰り上げる
 */
export const splitCardEndAt = (assignCheckoutDate: string, checkoutTime: string): { date: string; time: string } => {
  const endAt = dayjs(`${assignCheckoutDate.split(' ')[0]} ${checkoutTime}`)
  return { date: endAt.format('YYYY-MM-DD'), time: endAt.format('HH:mm') }
}
